<style lang="scss" scoped>
  .button {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    user-select: none;
    white-space: nowrap;
    border-radius: 8px;
    border: none;
    box-shadow: none;
    cursor: pointer;
    font-weight: 600;
    font-size: 1rem;
    font-family: 'VK Sans Display', 'Roboto', Arial, serif;
    transition: background-color .1s, color .1s, border-color .1s;

    &:not(.button-link) {
      padding: 12px 46px;

      @include media(max, md) {
        padding: 16px;
      }
    }

    @include media(hover) {
      transition: 100ms;
    }

    &, &:hover {
      text-decoration: none;
    }

    &-primary {
      background-image: linear-gradient(to bottom, $light-gold, $butterscotch);
      color: black;

      @include media(hover) {
        &:hover {
          background-image: linear-gradient(to bottom, rgba($light-gold, .9), rgba($butterscotch, .9));
        }
      }

      &:active {
        background-image: linear-gradient(to bottom, rgba($light-gold, .8), rgba($butterscotch, .8));
      }
    }

    &-secondary {
      background: $light-grey;
      color: black;
      font-weight: 500;

      @include media(hover) {
        &:hover {
          background-image: linear-gradient(0deg, rgba(0, 0, 0, 0.12), rgba(0, 0, 0, 0.12));
        }
      }

      &:active {
        background-image: linear-gradient(0deg, rgba(0, 0, 0, 0.32), rgba(0, 0, 0, 0.32));
      }
    }

    &-muted {
      background: $muted;
      color: #fff;
      font-weight: 500;

      @include media(hover) {
        &:hover {
          background-image: linear-gradient(0deg, rgba(0, 0, 0, 0.12), rgba(0, 0, 0, 0.12));
        }
      }

      &:active {
        background-image: linear-gradient(0deg, rgba(0, 0, 0, 0.32), rgba(0, 0, 0, 0.32));
      }
    }

    &-link {
      color: $link-color;
      background-color: transparent;
      transition: none;
      padding: 0;

      &:hover,
      &:focus,
      &:active,
      &.focus {
        text-decoration: none;
        border-color: transparent;
        box-shadow: none;
      }

      &:hover {
        color: $link-hover-color;
      }

      &:disabled,
      &.disabled {
        color: rgba(white, 0.6);
        pointer-events: none;
        background: none;
      }

      &.secondary {
        color: rgba($link-color, .5);

        &:hover {
          color: rgba($link-color, .76);
        }
      }

      &.secondary-bright {
        color: rgba($link-color, .76);

        &:hover {
          color: rgba($link-color, .9);
        }
      }
    }

    .icon {
      width: 1em;
      height: 1em;
      fill: currentColor;

      &-left {
        margin-right: .5rem;
      }

      &-right {
        margin-left: .5rem;
      }
    }

    &:focus:not(.button-link),
    &.focus:not(.button-link) {
      text-decoration: none;
      outline: 0;
    }

    &.disabled,
    &:disabled {
      &, &:hover, &:focus {
        background: #ccc;
        border-color: transparent;
        cursor: not-allowed;

        &:not(.button-loading) {
          color: rgba(white, .4);
        }
      }
    }

    &-loading {
      &, &.disabled, &:disabled {
        color: transparent;
      }

      .loader {
        position: absolute;
        transform: scale(0.5);
      }
    }

    &.fs-14 {
      font-size: 14px;
    }

    &.fs-18 {
      font-size: 18px;
    }

    &.fs-22 {
      font-size: 22px;
    }

    &.fs-24 {
      font-size: 24px;
    }

    &.fw-semi-bold {
      font-weight: 500;
    }

    &.fw-bold {
      font-weight: bold;
    }

    &.fw-black {
      font-weight: 900;
    }

    &.button-lg {
      &:not(.button-link) {
        padding: 17px 46px;
      }
    }

    &.button-xl {
      &:not(.button-link) {
        padding: 22px 46px;
      }

    }

    &.download {
      padding: 0!important;

      a {
        padding: 20px 40px;
        display: block;
        width: 100%;
        color: inherit;
        font-weight: 600;
        letter-spacing: 0.2px;
      }

      &.fs-22 {
        a {
          padding: 16px 46px;
        }
      }
    }

    &.button-block {
      width: 100%;

      + .button-block {
        margin-top: .5rem;
      }
    }

    .promo-page & {
      width: auto;
      border-right: 8px;
      font-family: 'VK Sans Display', Arial, serif;
      border-radius: 10px;
      font-size: 20px;
      font-weight: 600;
      line-height: 32px;
      color: #0F0F0F;
      padding: 13px 30px;

      @include media(max, lg) {
        width: 100%;
        font-size: 22px !important;
        font-weight: 600 !important;
        line-height: 18px;
        padding: 15px 70px !important;
      }
    }
  }
</style>

<template>
  <component
    :is="$attrs.href ? 'a' : (to ? 'router-link' : 'button')"
    :to="to"
    :disabled="disabled"
    :class="classes"
    :type="buttonType"
    v-on="listeners">
    <icon
      v-if="iconLeft"
      class="icon-left"
      :name="iconLeft"/>
    <slot/>
    <icon
      v-if="iconRight"
      class="icon-right"
      :name="iconRight"/>
  </component>
</template>

<script>
export default {
  name: 'pvp-button',
  props: {
    variant: {
      type: String,
      default: 'primary',
    },
    size: {
      type: String,
      default: '',
    },
    block: {
      type: Boolean,
      default: false,
    },
    iconLeft: {
      type: String,
      default: '',
    },
    iconRight: {
      type: String,
      default: '',
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    to: {
      type: [Object, String],
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: 'button',
    },
  },
  data: () => ({}),
  computed: {
    buttonType() {
      return this.to || this.$attrs.href ? null : this.type;
    },
    classes() {
      return [
        'button', {
          disabled: this.disabled || this.isLoading,
          [`button-${this.variant}`]: !!this.variant,
          [`button-${this.size}`]: !!this.size,
          'button-block': this.block,
          'button-loading': this.isLoading,
        },
      ];
    },
    listeners() {
      return this.disabled || this.isLoading ? undefined : this.$listeners;
    },
  },
};
</script>
